import { getApps } from "firebase/app";
import {
  getFirestore,
  collection,
  collectionGroup,
  WithFieldValue,
  FirestoreDataConverter,
  QueryDocumentSnapshot,
} from "firebase/firestore";

/**
 * Lazy initialization of Firestore
 * Only initializes when Firebase app is available
 */
const getFirestoreInstance = () => {
  const apps = getApps();
  if (apps.length === 0) {
    throw new Error(
      "Firebase app not initialized. Please call initializeApp() first."
    );
  }
  return getFirestore();
};

/**
 * Generic converter function for types without specific converters
 * Provides type safety for reads and writes
 */
const genericConverter = <T>() => ({
  toFirestore: (data: WithFieldValue<T>) => data,
  fromFirestore: (snap: QueryDocumentSnapshot) => snap.data() as T,
});

/**
 * Creates a typed collection reference with specific converter
 * @param collectionPath - The Firestore collection path
 * @param converter - The specific converter to use
 * @returns Typed collection reference with converter
 */
const dataPointWithConverter = <T>(
  collectionPath: string,
  converter: FirestoreDataConverter<T>
) =>
  collection(getFirestoreInstance(), collectionPath).withConverter(converter);

/**
 * Creates a typed collection reference with generic converter
 * @param collectionPath - The Firestore collection path
 * @returns Typed collection reference with generic converter
 */
const dataPoint = <T>(collectionPath: string) =>
  collection(getFirestoreInstance(), collectionPath).withConverter(
    genericConverter<T>()
  );

/**
 * Creates a typed collection group reference with specific converter
 * @param collectionId - The Firestore collection ID for collection group
 * @param converter - The specific converter to use
 * @returns Typed collection group reference with converter
 */
const collectionGroupDataPointWithConverter = <T>(
  collectionId: string,
  converter: FirestoreDataConverter<T>
) =>
  collectionGroup(getFirestoreInstance(), collectionId).withConverter(
    converter
  );

/**
 * Creates a typed collection group reference with generic converter
 * @param collectionId - The Firestore collection ID for collection group
 * @returns Typed collection group reference with generic converter
 */
const collectionGroupDataPoint = <T>(collectionId: string) =>
  collectionGroup(getFirestoreInstance(), collectionId).withConverter(
    genericConverter<T>()
  );

export {
  dataPointWithConverter,
  dataPoint,
  collectionGroupDataPointWithConverter,
  collectionGroupDataPoint,
};
